import {
	FaBluesky,
	FaCopy,
	FaLinkedin,
	FaMastodon,
	FaThreads,
	FaTwitter,
} from "react-icons/fa6";
import { Button } from "@/components/ui/button";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/utils/clipboard";

export default function ShareLink({
	text,
	url,
}: {
	text: string;
	url: string;
}) {
	type ShareItem = {
		name: string;
		icon: React.ComponentType<{ className?: string }>;
		url: string;
		color: string;
	};

	const twitterHandleFrom = (input?: string) => {
		if (!input) return undefined;
		try {
			if (input.startsWith("http")) {
				const u = new URL(input);
				const handle = u.pathname.replace(/^\/+/, "").split("/")[0];
				return handle?.replace(/^@/, "") || undefined;
			}
		} catch {}
		return input.replace(/^@/, "");
	};

	const twitterHandle = twitterHandleFrom(config.Socials?.Twitter);

	const shareData: ShareItem[] = [];

	shareData.push({
		name: "Twitter",
		icon: FaTwitter,
		url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}${
			twitterHandle ? `&via=${encodeURIComponent(twitterHandle)}` : ""
		}`,
		color: "hover:bg-blue-500/10 hover:text-blue-500",
	});

	shareData.push({
		name: "LinkedIn",
		icon: FaLinkedin,
		url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
		color: "hover:bg-blue-600/10 hover:text-blue-600",
	});

	shareData.push({
		name: "Threads",
		icon: FaThreads,
		url: `https://www.threads.net/intent/post?text=${encodeURIComponent(`${text} ${url}`)}`,
		color:
			"hover:bg-black/10 hover:text-black dark:hover:bg-white/10 dark:hover:text-white",
	});

	shareData.push({
		name: "Bluesky",
		icon: FaBluesky,
		url: `https://bsky.app/intent/compose?text=${encodeURIComponent(`${text} ${url}`)}`,
		color: "hover:bg-sky-500/10 hover:text-sky-500",
	});

	shareData.push({
		name: "Mastodon",
		icon: FaMastodon,
		url: `https://mastodonshare.com/?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
		color: "hover:bg-purple-500/10 hover:text-purple-500",
	});

	shareData.push({
		name: "Copy URL",
		icon: FaCopy,
		url,
		color: "hover:bg-green-500/10 hover:text-green-500",
	});

	const handleShare = (shareUrl: string) => {
		window.open(shareUrl, "_blank");
	};

	const handleCopy = (url: string) => {
		void copyToClipboard(url);
	};

	return (
		<div className="flex flex-col items-center space-y-4 mt-12">
			<h3 className="text-lg font-semibold text-foreground mb-4">
				Share this article
			</h3>

			{/* Desktop */}
			<div className="hidden md:flex items-center gap-2">
				{shareData.map((platform) => {
					const IconComponent = platform.icon;
					return (
						<Button
							key={platform.name}
							variant="outline"
							size="sm"
							onClick={() =>
								platform.name === "Copy URL"
									? handleCopy(platform.url)
									: handleShare(platform.url)
							}
							className={cn(
								"flex items-center gap-2 transition-colors cursor-pointer",
								platform.color,
							)}
						>
							<IconComponent className="h-4 w-4" />
							{platform.name}
						</Button>
					);
				})}
			</div>

			{/* Mobile */}
			<div className="grid grid-cols-2 gap-2 md:hidden w-full max-w-sm">
				{shareData.map((platform) => {
					const IconComponent = platform.icon;
					return (
						<Button
							key={platform.name}
							variant="outline"
							size="sm"
							onClick={() =>
								platform.name === "Copy URL"
									? handleCopy(platform.url)
									: handleShare(platform.url)
							}
							className={cn(
								"flex items-center gap-2 justify-center transition-colors",
								platform.color,
							)}
						>
							<IconComponent className="h-4 w-4" />
							<span className="text-xs">{platform.name}</span>
						</Button>
					);
				})}
			</div>
		</div>
	);
}
